import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import OpenPlayToolsLayout from '../../components/openplay/OpenPlayToolsLayout';
import { resolveBoardConfig } from './communityConfig';
import { supabase } from '../../lib/supabaseClient';

const MAX_IMAGE_BYTES = 5 * 1024 * 1024;

const getSafeImageExtension = (file) => {
  const mime = String(file?.type || '').toLowerCase();
  if (mime === 'image/jpeg') return 'jpg';
  if (mime === 'image/png') return 'png';
  if (mime === 'image/webp') return 'webp';
  if (mime === 'image/gif') return 'gif';
  if (mime === 'image/heic') return 'heic';
  if (mime === 'image/heif') return 'heif';

  const name = String(file?.name || '');
  const match = name.match(/\.([a-zA-Z0-9]+)$/);
  if (match?.[1]) return match[1].toLowerCase();
  return 'jpg';
};

const defaultFormState = {
  title: '',
  content: '',
};

const resolveNaverMapsCredential = ({ clientId, ncpKeyId, clientParam }) => {
  if (ncpKeyId) return { param: 'ncpKeyId', value: ncpKeyId };
  if (!clientId) return null;
  if (clientParam === 'ncpKeyId') return { param: 'ncpKeyId', value: clientId };
  if (clientParam === 'clientId') return { param: 'clientId', value: clientId };
  return { param: 'ncpClientId', value: clientId };
};

const loadNaverMapsScript = ({ param, value }) =>
  new Promise((resolve, reject) => {
    if (typeof window === 'undefined') {
      reject(new Error('No window'));
      return;
    }

    if (!param || !value) {
      reject(new Error('Missing Naver Maps credential'));
      return;
    }

    if (window.naver?.maps) {
      resolve();
      return;
    }

    const desiredSrc = `https://oapi.map.naver.com/openapi/v3/maps.js?${param}=${encodeURIComponent(value)}&submodules=geocoder`;

    const existing = document.querySelector('script[data-naver-maps-sdk="true"]');
    if (existing) {
      const existingSrc = existing.getAttribute('src') || '';
      if (existingSrc && existingSrc !== desiredSrc) {
        try {
          existing.parentNode?.removeChild(existing);
        } catch {
          // ignore
        }
      } else {
        existing.addEventListener('load', () => resolve());
        existing.addEventListener('error', () => reject(new Error('Failed to load Naver Maps SDK')));
        return;
      }
    }

    const script = document.createElement('script');
    script.dataset.naverMapsSdk = 'true';
    script.async = true;
    script.defer = true;
    script.src = desiredSrc;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load Naver Maps SDK script: ${script.src}`));
    document.head.appendChild(script);
  });

const CommunityFormPage = () => {
  const params = useParams();
  const navigate = useNavigate();
  const boardKey = String(params?.board || 'matching');
  const postId = params?.postId ? String(params.postId) : null;
  const config = resolveBoardConfig(boardKey);
  const isMatchingBoard = useMemo(() => boardKey === 'matching', [boardKey]);
  const isLocationEnabled = useMemo(() => boardKey === 'matching' || boardKey === 'free', [boardKey]);

  const [selectedItem, setSelectedItem] = useState(null);
  const [formState, setFormState] = useState(defaultFormState);
  const [images, setImages] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [removingImages, setRemovingImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [authUser, setAuthUser] = useState(null);

  const [naverMapsStatus, setNaverMapsStatus] = useState('idle');
  const [naverMapsError, setNaverMapsError] = useState(null);
  const [neighborhoodName, setNeighborhoodName] = useState('');
  const [neighborhoodCoords, setNeighborhoodCoords] = useState(null);
  const [isLocatingNeighborhood, setIsLocatingNeighborhood] = useState(false);

  useEffect(() => {
    const init = async () => {
      const { data } = await supabase.auth.getSession();
      const user = data?.session?.user || null;
      if (!user) {
        navigate('/openplay/user-authentication');
        return;
      }
      setAuthUser(user);

      if (!postId) {
        setLoading(false);
        return;
      }

      const { data: row, error } = await supabase
        .from('community_posts')
        .select('id, author_id, author_name, title, content, image_urls, neighborhood_name, neighborhood_lat, neighborhood_lng')
        .eq('id', postId)
        .maybeSingle();
      if (error || !row) {
        alert(error?.message || '게시글을 불러오지 못했습니다.');
        navigate(`/openplay/community/${boardKey}`);
        return;
      }
      if (row.author_id !== user.id) {
        alert('본인의 글만 수정할 수 있습니다.');
        navigate(`/openplay/community/${boardKey}/${postId}`);
        return;
      }
      setSelectedItem(row);
      setFormState({
        title: row.title || '',
        content: row.content || '',
      });
      setExistingImages(Array.isArray(row.image_urls) ? row.image_urls : []);
      setNeighborhoodName(String(row.neighborhood_name || ''));
      if (typeof row.neighborhood_lat === 'number' && typeof row.neighborhood_lng === 'number') {
        setNeighborhoodCoords({ lat: row.neighborhood_lat, lng: row.neighborhood_lng });
      }
      setLoading(false);
    };

    init();
  }, [boardKey, navigate, postId]);

  useEffect(() => {
    const previews = images.map((file) => ({ file, url: URL.createObjectURL(file) }));
    setImagePreviews(previews);
    return () => {
      previews.forEach((preview) => URL.revokeObjectURL(preview.url));
    };
  }, [images]);

  useEffect(() => {
    if (!isLocationEnabled) return;

    const naverMapsClientId = import.meta.env.VITE_NAVER_MAPS_CLIENT_ID;
    const naverMapsNcpKeyId = import.meta.env.VITE_NAVER_MAPS_NCP_KEY_ID;
    const naverMapsClientParam = import.meta.env.VITE_NAVER_MAPS_CLIENT_PARAM;
    const credential = resolveNaverMapsCredential({
      clientId: naverMapsClientId,
      ncpKeyId: naverMapsNcpKeyId,
      clientParam: naverMapsClientParam,
    });

    if (!credential) {
      setNaverMapsStatus('missing_key');
      return;
    }

    let cancelled = false;

    const init = async () => {
      try {
        setNaverMapsError(null);
        setNaverMapsStatus('loading');
        await loadNaverMapsScript(credential);
        if (cancelled) return;
        if (!window.naver?.maps) throw new Error('Naver Maps SDK not available after script load');

        setNaverMapsStatus('ready');
      } catch (e) {
        if (cancelled) return;
        setNaverMapsError(e);
        setNaverMapsStatus('error');
      }
    };

    init();

    return () => {
      cancelled = true;
    };
  }, [isLocationEnabled]);

  const reverseGeocodeNeighborhood = async ({ lat, lng }) => {
    if (!window.naver?.maps?.Service?.reverseGeocode) return null;

    const naver = window.naver;
    return new Promise((resolve) => {
      try {
        naver.maps.Service.reverseGeocode(
          {
            coords: new naver.maps.LatLng(lat, lng),
            orders: [naver.maps.Service.OrderType.ADDR, naver.maps.Service.OrderType.ROAD_ADDR].join(','),
          },
          (status, response) => {
            if (status !== naver.maps.Service.Status.OK) {
              resolve(null);
              return;
            }
            const results = response?.v2?.results;
            const region = results?.[0]?.region;
            const area1 = region?.area1?.name;
            const area2 = region?.area2?.name;
            const area3 = region?.area3?.name;
            const area4 = region?.area4?.name;
            const name = [area1, area2, area3, area4].filter(Boolean).join(' ');
            resolve(name || null);
          }
        );
      } catch {
        resolve(null);
      }
    });
  };

  const handleUseCurrentNeighborhood = async () => {
    if (!isLocationEnabled) return;
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      alert('이 브라우저에서는 위치 기능을 사용할 수 없습니다.');
      return;
    }

    if (naverMapsStatus !== 'ready') {
      alert('네이버 지도 설정을 확인해주세요.');
      return;
    }

    setIsLocatingNeighborhood(true);
    try {
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 30000,
        });
      });
      const lat = position?.coords?.latitude;
      const lng = position?.coords?.longitude;
      if (typeof lat !== 'number' || typeof lng !== 'number') {
        alert('현재 위치를 확인할 수 없습니다.');
        return;
      }
      const name = await reverseGeocodeNeighborhood({ lat, lng });
      if (!name) {
        alert('현재 위치의 주소를 가져오지 못했습니다. 잠시 후 다시 시도해주세요.');
        return;
      }
      setNeighborhoodCoords({ lat, lng });
      setNeighborhoodName(name);
    } catch (e) {
      const code = e?.code;
      if (code === 1) {
        alert('위치 권한이 거부되었습니다. 브라우저/OS 설정에서 이 사이트의 위치 권한을 허용해주세요.');
        return;
      }
      if (code === 2) {
        alert('위치 정보를 확인할 수 없습니다. OS 위치 서비스가 켜져있는지, Wi‑Fi/네트워크 상태를 확인해주세요.');
        return;
      }
      if (code === 3) {
        alert('위치 확인 시간이 초과되었습니다. 잠시 후 다시 시도해주세요.');
        return;
      }
      alert('현재 위치를 가져오지 못했습니다.');
    } finally {
      setIsLocatingNeighborhood(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const title = formState.title.trim();
    const content = formState.content.trim();

    if (!title || !content) {
      alert('제목과 내용을 모두 입력해주세요.');
      return;
    }

    if (isMatchingBoard && !neighborhoodName) {
      alert('동네를 선택해주세요.');
      return;
    }

    if (!authUser) {
      alert('로그인이 필요합니다.');
      navigate('/openplay/user-authentication');
      return;
    }

    if (!import.meta.env.VITE_SUPABASE_URL || !import.meta.env.VITE_SUPABASE_ANON_KEY) {
      alert('Supabase 환경변수(VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY)가 설정되어야 합니다.');
      return;
    }

    setSubmitting(true);
    try {
      const authorName =
        authUser?.user_metadata?.name ||
        authUser?.user_metadata?.nickname ||
        (authUser?.email ? authUser.email.split('@')[0] : '') ||
        '익명';
      let finalImageUrls = existingImages.filter((url) => !removingImages.includes(url));

      if (images.length > 0) {
        try {
          const uploads = [];
          for (const file of images) {
            if (file?.size && file.size > MAX_IMAGE_BYTES) {
              throw new Error('첨부파일은 5MB 이하만 업로드할 수 있습니다.');
            }
            const ext = getSafeImageExtension(file);
            const path = `${authUser.id}/community/${crypto.randomUUID()}.${ext}`;
            const { error: uploadError } = await supabase.storage
              .from('community-images')
              .upload(path, file, { cacheControl: '3600', upsert: false, contentType: file.type || undefined });
            if (uploadError) throw uploadError;
            const { data: publicData } = supabase.storage.from('community-images').getPublicUrl(path);
            const publicUrl = String(publicData?.publicUrl || '').trim();
            if (publicUrl) uploads.push(publicUrl);
          }
          finalImageUrls = [...finalImageUrls, ...uploads];
        } catch (e) {
          console.group('[Community] image upload failed');
          console.error(e);
          console.groupEnd();
          throw e;
        }
      }

      if (selectedItem) {
        try {
          const { error } = await supabase
            .from('community_posts')
            .update({
              title,
              content,
              author_name: authorName,
              image_urls: finalImageUrls,
              ...(isLocationEnabled
                ? {
                    neighborhood_name: neighborhoodName || null,
                    neighborhood_lat: neighborhoodCoords?.lat ?? null,
                    neighborhood_lng: neighborhoodCoords?.lng ?? null,
                  }
                : {}),
            })
            .eq('id', selectedItem.id)
            .eq('author_id', authUser.id);
          if (error) throw error;
          navigate(`/openplay/community/${boardKey}/${selectedItem.id}`);
          return;
        } catch (e) {
          console.group('[Community] post update failed');
          console.error(e);
          console.groupEnd();
          throw e;
        }
      }

      try {
        const { data: created, error } = await supabase
          .from('community_posts')
          .insert({
            board_key: boardKey,
            title,
            content,
            author_id: authUser.id,
            author_name: authorName,
            image_urls: finalImageUrls,
            ...(isLocationEnabled
              ? {
                  neighborhood_name: neighborhoodName || null,
                  neighborhood_lat: neighborhoodCoords?.lat ?? null,
                  neighborhood_lng: neighborhoodCoords?.lng ?? null,
                }
              : {}),
          })
          .select('id')
          .single();
        if (error) throw error;
        navigate(`/openplay/community/${boardKey}/${created.id}`);
      } catch (e) {
        console.group('[Community] post insert failed');
        console.error(e);
        console.groupEnd();
        throw e;
      }
    } catch (err) {
      if (err instanceof TypeError && String(err?.message || '').toLowerCase().includes('failed to fetch')) {
        alert(
          '네트워크 오류로 저장에 실패했습니다.\n\n- Supabase URL/KEY 설정 확인\n- 로컬/사내망 프록시/광고차단 확장프로그램 확인\n- 콘솔(Network)에서 실패한 요청 확인'
        );
        return;
      }
      alert(err?.message || '저장에 실패했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <OpenPlayToolsLayout userRole="player">
      <main className="pt-6 md:pt-10 pb-[calc(84px+env(safe-area-inset-bottom,16px))] md:pb-10">
        <div className="max-w-4xl mx-auto px-4 md:px-6 lg:px-8">
          <div className="mb-6 md:mb-8">
            <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-2">
              {config.label} {selectedItem ? '수정' : '작성'}
            </h1>
            <p className="text-base md:text-lg caption text-muted-foreground">게시글 정보를 입력해주세요.</p>
          </div>

          <div className="bg-card border border-border rounded-xl p-6">
            {loading ? (
              <div className="text-sm text-muted-foreground">불러오는 중...</div>
            ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="text-xs font-semibold text-muted-foreground">제목</label>
                <input
                  value={formState.title}
                  onChange={(event) => setFormState((prev) => ({ ...prev, title: event.target.value }))}
                  className="mt-1 w-full h-11 rounded-lg border border-border bg-background px-3 text-sm"
                  placeholder="제목을 입력하세요"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-muted-foreground">내용</label>
                <textarea
                  value={formState.content}
                  onChange={(event) => setFormState((prev) => ({ ...prev, content: event.target.value }))}
                  className="mt-1 w-full min-h-[180px] rounded-lg border border-border bg-background px-3 py-2 text-sm"
                  placeholder="게시글 내용을 입력하세요"
                />
              </div>
              {isLocationEnabled && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <label className="text-xs font-semibold text-muted-foreground">위치</label>
                    <button
                      type="button"
                      onClick={handleUseCurrentNeighborhood}
                      disabled={isLocatingNeighborhood || naverMapsStatus !== 'ready'}
                      className="h-9 px-3 rounded-lg border border-border text-xs text-muted-foreground disabled:opacity-70"
                    >
                      {isLocatingNeighborhood ? '위치 확인 중...' : '현재 위치로 설정'}
                    </button>
                  </div>
                  {naverMapsStatus === 'missing_key' ? (
                    <div className="text-xs text-destructive">
                      네이버 지도 Client ID가 필요합니다. (.env: VITE_NAVER_MAPS_CLIENT_ID)
                    </div>
                  ) : naverMapsStatus === 'error' ? (
                    <div className="text-xs text-destructive">지도를 불러오지 못했습니다. {naverMapsError?.message}</div>
                  ) : (
                    <>
                      <input
                        value={neighborhoodName}
                        readOnly
                        className="w-full h-11 rounded-lg border border-border bg-background px-3 text-sm"
                        placeholder="현재 위치로 주소를 설정해주세요"
                      />
                      <div className="text-xs text-muted-foreground">현재 위치를 가져와 주소(행정구역)를 자동으로 입력합니다.</div>
                    </>
                  )}
                </div>
              )}
              <div>
                <label className="text-xs font-semibold text-muted-foreground">이미지 첨부</label>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={(event) => {
                    const next = Array.from(event.target.files || []);
                    const tooLarge = next.filter((file) => file?.size && file.size > MAX_IMAGE_BYTES);
                    if (tooLarge.length > 0) {
                      alert('첨부파일은 5MB 이하만 업로드할 수 있습니다.');
                    }
                    setImages(next.filter((file) => !file?.size || file.size <= MAX_IMAGE_BYTES));
                  }}
                  className="mt-2 text-sm"
                />
                {(existingImages.length > 0 || imagePreviews.length > 0) && (
                  <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-3">
                    {existingImages.map((url) => (
                      <div key={url} className="relative border border-border rounded-lg overflow-hidden">
                        <img src={url} alt="existing" className="w-full h-24 object-cover" />
                        <button
                          type="button"
                          onClick={() => setRemovingImages((prev) => [...prev, url])}
                          className="absolute top-2 right-2 text-xs bg-background/80 px-2 py-1 rounded"
                        >
                          제거
                        </button>
                      </div>
                    ))}
                    {imagePreviews.map((preview) => (
                      <div key={preview.url} className="border border-border rounded-lg overflow-hidden">
                        <img src={preview.url} alt="preview" className="w-full h-24 object-cover" />
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 h-11 rounded-lg bg-primary text-primary-foreground text-sm font-semibold disabled:opacity-70"
                >
                  {submitting ? '저장 중...' : selectedItem ? '수정 완료' : '등록'}
                </button>
                <Link
                  to={selectedItem ? `/openplay/community/${boardKey}/${selectedItem.id}` : `/openplay/community/${boardKey}`}
                  className="h-11 px-4 rounded-lg border border-border text-sm text-muted-foreground flex items-center justify-center"
                >
                  취소
                </Link>
              </div>
            </form>
            )}
          </div>
        </div>
      </main>
    </OpenPlayToolsLayout>
  );
};

export default CommunityFormPage;
